<script lang="ts">
  import { fade } from "svelte/transition";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import DiscussionItemThread from "./DiscussionItemThread.svelte";
  import DiscussionItemReply from "./DiscussionItemReply.svelte";
  import DiscussionHeader from "./DiscussionHeader.svelte";
  import { communityFlag, communitySlug } from "$stores/community";
  import { docURL } from "$utils/urls";
  import { slugify } from "$utils/strings";
  import { RelatedItem, Category, Doc } from "$components/+blocks/OVIcon";
  import { track } from "app/utils/analytics";
  import type { SemanticRelations, FolderList } from "@outverse-video/outverse-api-client-lib";

  export let relations: SemanticRelations;

  // backend typing is messed up and cannot be easily fixed, so using this workaround
  const castFolderList = (folder: any) => folder as FolderList;
</script>

<div
  class="scrollbar-thin overflow-x-hidden overflow-y-auto scrollbar-invisible pb-8 font-medium hover:scrollbar-visible"
  in:fade|global={{ duration: 50 }}
>
  <!-- Docs -->
  {#if relations.documents.length}
    <div class="py-8">
      <div class="mb-5 flex items-center px-6 text-sm md:text-[13px]">
        <OVIcon icon={RelatedItem} class="text-gray-500/60 dark:text-gray-400 " />
        <span class="ml-1 text-gray-500/60 dark:text-gray-400"> Related docs</span>
        <span class="mr-auto ml-2 text-gray-400/80 dark:text-gray-400/60"
          >{relations.documents.length}</span
        >
      </div>

      <div class="flex flex-col gap-2 px-6 pb-2">
        {#each relations.documents as document (document.uuid)}
          <a
            class="in-out-shadow flex w-full cursor-pointer flex-col gap-1.5 rounded-md bg-black/2 px-2 py-2.5 transition-colors after:rounded-md hover:bg-white dark:bg-white/3 dark:hover:bg-white/8"
            href={docURL({
              communitySlug: $communitySlug,
              docUuid: document.uuid,
              docSlug: slugify(document.title ?? ""),
            })}
            on:click={() => track("Sidebar Semantic Relation Clicked")}
          >
            <div class="flex items-center gap-1.5">
              <OVIcon icon={Doc} size="size-3.5" class="shrink-0 text-gray-400" />
              <span class="truncate text-sm text-gray-500 md:text-[13px] dark:text-gray-300"
                >{document.title}</span
              >
            </div>
            <div class="ml-[1px] flex items-center gap-1.5 text-gray-400">
              <OVIcon icon={Category} size="size-3.5" />
              <span class="text-xs">{castFolderList(document.folder).title}</span>
            </div>
          </a>
        {/each}
      </div>
    </div>
  {/if}

  <!-- Discussions -->
  {#if relations.discussions.length && $communityFlag}
    <Separator />
    <div class="mt-8 flex items-center px-6 text-sm md:text-[13px]">
      <OVIcon icon={RelatedItem} class="text-gray-500/60 dark:text-gray-400" />
      <span class="ml-1 text-gray-500/60 dark:text-gray-400">Related threads</span>
      <span class="mr-auto ml-2 text-gray-400/80 dark:text-gray-400/60"
        >{relations.discussions.length}</span
      >
    </div>

    <div class="mt-5 flex flex-col gap-7 px-6">
      {#each relations.discussions as discussion}
        <div>
          <DiscussionHeader
            discussionThread={discussion.thread}
            discussionItemsCount={discussion.discussionItems.length}
          />

          <!-- DiscussionItems -->
          <div class="in-out-shadow rounded-md bg-black/2 after:rounded-md dark:bg-white/3">
            {#each discussion.discussionItems as discussionItem, index (discussionItem.uuid)}
              {#if discussionItem.discussionItemType === "ForumThread"}
                <div class="p-1">
                  <DiscussionItemThread thread={discussionItem} />
                </div>
              {/if}
              {#if discussionItem.discussionItemType === "ThreadPost"}
                <div class="p-1">
                  <DiscussionItemReply reply={discussionItem} />
                </div>
              {/if}
              {#if index < discussion.discussionItems.length - 1}
                <Separator bgColor="dark:bg-white/8 bg-black/8 mx-[1px]" />
              {/if}
            {/each}
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>
